import type { SorterResult } from "antd/lib/table/interface";
import { type ExtendedColumnsType, GenericTable, type ResourceTableProps } from "components";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import {
    displayDateTimeValue,
    displayStatusValue,
} from "../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { ResourceName } from "../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../core/router/types/routerTypes";
import type { PaymentMethodProfile } from "../../../types/entities/paymentMethodProfile";

export const PaymentMethodProfilesTable: FC<ResourceTableProps<PaymentMethodProfile>> = ({
    onStateChanged,
    onRowSelectionChange,
    datasource,
}) => {
    const { t } = useTranslation();
    const getColumnsConfig = useCallback(
        (sortInfo: SorterResult<PaymentMethodProfile>): ExtendedColumnsType<PaymentMethodProfile>[] => {
            return [
                {
                    title: t("mopProfileList.id"),
                    key: "id",
                    dataIndex: "id",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
                },
                {
                    title: t("mopProfileList.name"),
                    key: "name",
                    dataIndex: "name",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "name" ? sortInfo.order : null,
                },
                {
                    title: t("mopProfileList.organizationUnits"),
                    key: "organizationUnits",
                    dataIndex: "organizationUnits",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "organizationUnit" ? sortInfo.order : null,
                },
                {
                    title: t("mopProfileList.status"),
                    key: "status",
                    dataIndex: "status",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "status" ? sortInfo.order : null,
                    render: (value) => displayStatusValue(value),
                },
                {
                    title: t("mopProfileList.changed"),
                    dataIndex: "changed",
                    key: "changed",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "changed" ? sortInfo.order : null,
                    render: (value) => displayDateTimeValue(value),
                },
            ];
        },
        [t],
    );

    const navigate = useNavigate();
    const onRowClick = (record: PaymentMethodProfile) =>
        navigate(`${BASE_PATH}/${ResourceName.PAYMENT_METHOD_PROFILES}/${FORM_ACTION.EDIT}/${record.id}`);

    return (
        (datasource && (
            <GenericTable<PaymentMethodProfile>
                getColumnsConfig={getColumnsConfig}
                datasource={datasource}
                onSelectionCallback={onRowSelectionChange}
                onStateChanged={onStateChanged}
                onRow={(record: PaymentMethodProfile) => {
                    return {
                        onClick: () => onRowClick(record),
                    };
                }}
            />
        )) ||
        null
    );
};
